/**
 * ProductDetailPage.tsx
 * Full product detail with gallery, specs, reviews, and inquiry options
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProduct, useProductReviews } from '../hooks/useMarketplace';
import { useMarketplaceStore } from '../stores/marketplaceStore';
import { 
  ProductGallery, 
  ProductSpecs, 
  PriceDisplay, 
  StarRating,
  ReviewList,
  ReviewForm,
  InquiryForm,
  QuoteRequestForm 
} from '../components';

type TabType = 'description' | 'specs' | 'reviews';

export const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('description');
  const [showInquiry, setShowInquiry] = useState(false);
  const [showQuoteRequest, setShowQuoteRequest] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);

  const { data: product, isLoading, error } = useProduct(slug || '');
  const { data: reviewsData } = useProductReviews(product?.id || '', { page: 1, limit: 5 });
  
  const { addRecentlyViewedProduct, toggleWishlist, isInWishlist } = useMarketplaceStore();

  // Track view
  useEffect(() => {
    if (product) {
      addRecentlyViewedProduct(product.id);
    }
  }, [product, addRecentlyViewedProduct]);

  const inWishlist = product ? isInWishlist(product.id) : false;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-4 w-48 bg-[#F5EDE4] rounded mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-[#F5EDE4] rounded-2xl" />
              <div className="space-y-6">
                <div className="h-8 w-3/4 bg-[#F5EDE4] rounded" />
                <div className="h-6 w-1/2 bg-[#F5EDE4] rounded" />
                <div className="h-24 bg-[#F5EDE4] rounded" />
                <div className="h-12 w-48 bg-[#F5EDE4] rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[#2B3A4D] mb-2">Product Not Found</h1>
          <p className="text-[#8B8178] mb-6">The product you're looking for doesn't exist.</p>
          <Link
            to="/marketplace"
            className="inline-flex items-center px-6 py-3 bg-[#7A9E7E] text-white rounded-xl hover:bg-[#5C7D60] transition-colors"
          >
            Browse Marketplace
          </Link>
        </div>
      </div>
    );
  }

  const tabs: { id: TabType; label: string; count?: number }[] = [
    { id: 'description', label: 'Description' },
    { id: 'specs', label: 'Specifications' },
    { id: 'reviews', label: 'Reviews', count: product.review_count },
  ];

  return (
    <div className="min-h-screen bg-[#FDF8F3] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-[#8B8178] mb-8">
          <Link to="/marketplace" className="hover:text-[#7A9E7E]">Marketplace</Link>
          <span>/</span>
          <Link to={`/marketplace/category/${product.category?.slug}`} className="hover:text-[#7A9E7E]">
            {product.category?.name || 'Products'}
          </Link>
          <span>/</span>
          <span className="text-[#2B3A4D]">{product.name}</span>
        </nav>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Gallery */}
          <div>
            <ProductGallery images={product.image_urls || []} productName={product.name} />
          </div>

          {/* Product Info */}
          <div>
            {/* Badges */}
            <div className="flex items-center gap-2 mb-4">
              {product.is_featured && (
                <span className="px-3 py-1 bg-[#C4785A]/10 text-[#C4785A] text-sm font-medium rounded-full">
                  Featured
                </span>
              )}
              {product.accepts_insurance && (
                <span className="px-3 py-1 bg-[#7A9E7E]/10 text-[#5C7D60] text-sm font-medium rounded-full">
                  Insurance Accepted
                </span>
              )}
            </div>

            <h1 className="text-3xl font-semibold text-[#2B3A4D] font-['Fraunces'] mb-2">
              {product.name}
            </h1>

            {/* Vendor Link */}
            {product.vendor && (
              <Link 
                to={`/marketplace/vendors/${product.vendor.slug}`}
                className="text-[#7A9E7E] hover:text-[#5C7D60] font-medium mb-4 inline-block"
              >
                by {product.vendor.name}
              </Link>
            )}

            {/* Rating */}
            <div className="flex items-center gap-3 mb-6">
              <StarRating rating={product.avg_rating ?? 0} size="md" />
              <span className="font-medium text-[#2B3A4D]">{(product.avg_rating ?? 0).toFixed(1)}</span>
              <span className="text-[#8B8178]">({product.review_count ?? 0} reviews)</span>
            </div>

            {/* Price */}
            <div className="mb-6">
              <PriceDisplay
                priceType={product.price_type}
                priceMin={product.price_min}
                priceMax={product.price_max}
                acceptsInsurance={product.accepts_insurance}
                size="lg"
              />
              {product.insurance_notes && (
                <p className="text-sm text-[#8B8178] mt-2">{product.insurance_notes}</p>
              )}
            </div>

            {/* Short Description */}
            <p className="text-[#445566] mb-8">{product.short_description}</p>

            {/* Actions */}
            <div className="flex flex-wrap gap-4 mb-8">
              {product.price_type === 'quote' ? (
                <button
                  onClick={() => setShowQuoteRequest(true)}
                  className="px-8 py-4 bg-[#7A9E7E] text-white rounded-xl font-semibold hover:bg-[#5C7D60] transition-colors"
                >
                  Request Quote
                </button>
              ) : (
                <button
                  onClick={() => setShowInquiry(true)}
                  className="px-8 py-4 bg-[#7A9E7E] text-white rounded-xl font-semibold hover:bg-[#5C7D60] transition-colors"
                >
                  Contact Vendor
                </button>
              )}
              
              <button
                onClick={() => product && toggleWishlist(product.id)}
                className={`p-4 rounded-xl border-2 transition-colors ${
                  inWishlist
                    ? 'border-[#C4785A] bg-[#C4785A]/5 text-[#C4785A]'
                    : 'border-[#E5E0DB] text-[#8B8178] hover:border-[#C4785A] hover:text-[#C4785A]'
                }`}
              >
                <svg className="w-6 h-6" fill={inWishlist ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            </div>

            {/* Features */}
            {product.features && product.features.length > 0 && (
              <div className="border-t border-[#E5E0DB] pt-6">
                <h3 className="font-medium text-[#2B3A4D] mb-3">Key Features</h3>
                <ul className="space-y-2">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-[#445566]">
                      <svg className="w-5 h-5 text-[#7A9E7E] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="border-b border-[#E5E0DB]">
            <div className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 font-medium transition-colors relative ${
                    activeTab === tab.id
                      ? 'text-[#7A9E7E]'
                      : 'text-[#8B8178] hover:text-[#2B3A4D]'
                  }`}
                >
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className="ml-2 text-sm text-[#8B8178]">({tab.count})</span>
                  )}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#7A9E7E]" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="p-8">
            {activeTab === 'description' && (
              <div className="prose prose-lg max-w-none">
                <p className="text-[#445566] whitespace-pre-wrap">{product.description}</p>
              </div>
            )}

            {activeTab === 'specs' && product.specifications && (
              <ProductSpecs specifications={product.specifications} />
            )}

            {activeTab === 'reviews' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-[#2B3A4D]">Customer Reviews</h3>
                  <button
                    onClick={() => setShowReviewForm(true)}
                    className="px-4 py-2 bg-[#7A9E7E] text-white rounded-lg font-medium hover:bg-[#5C7D60] transition-colors"
                  >
                    Write a Review
                  </button>
                </div>
                
                {reviewsData && reviewsData.items.length > 0 ? (
                  <ReviewList 
                    productId={product.id}
                    initialData={reviewsData}
                  />
                ) : (
                  <div className="text-center py-12">
                    <p className="text-[#8B8178] mb-4">No reviews yet. Be the first to review!</p>
                    <button
                      onClick={() => setShowReviewForm(true)}
                      className="text-[#7A9E7E] font-medium hover:text-[#5C7D60]"
                    >
                      Write a Review
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inquiry Modal */}
      {showInquiry && product.vendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E5E0DB] flex items-center justify-between">
              <h3 className="text-xl font-semibold text-[#2B3A4D]">Inquire About {product.name}</h3>
              <button onClick={() => setShowInquiry(false)} className="p-2 hover:bg-[#F5EDE4] rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <InquiryForm 
                vendorId={product.vendor.id}
                vendorName={product.vendor.name}
                productId={product.id}
                productName={product.name}
                onSuccess={() => setShowInquiry(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Quote Request Modal */}
      {showQuoteRequest && product.vendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E5E0DB] flex items-center justify-between">
              <h3 className="text-xl font-semibold text-[#2B3A4D]">Request Quote</h3>
              <button onClick={() => setShowQuoteRequest(false)} className="p-2 hover:bg-[#F5EDE4] rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <QuoteRequestForm
                vendorId={product.vendor.id}
                vendorName={product.vendor.name}
                productId={product.id}
                productName={product.name}
                onSuccess={() => setShowQuoteRequest(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Review Form Modal */}
      {showReviewForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E5E0DB] flex items-center justify-between">
              <h3 className="text-xl font-semibold text-[#2B3A4D]">Write a Review</h3>
              <button onClick={() => setShowReviewForm(false)} className="p-2 hover:bg-[#F5EDE4] rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <ReviewForm
                productId={product.id}
                productName={product.name}
                onSuccess={() => setShowReviewForm(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;


